// src/api/campaignExclusionService.js
// Service for handling campaign exclusions based on frequency limits and recurring campaign settings

import {
  doc,
  getDoc,
  addDoc,
  collection,
  getDocs,
  query,
  where,
  Timestamp
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getCustomerTargetingPreview } from './salonApi';

/**
 * Count deliveries per customer from the campaignDeliveries collection.
 * Each record is written by recordCampaignDeliveries() after every actual blast.
 * Uses the composite index (campaignType, salonId, sentAt) defined in firestore.indexes.json.
 *
 * @returns {{ [customerId: string]: number }} map of customerId to delivery count
 */
const countDeliveriesFromCampaignCollection = async (salonId, campaignType, startTime, customerList) => {
  try {
    // Query the campaignDeliveries collection instead of raw campaign collections
    // (pushNotifications/mail/whatsappMessages). campaignDeliveries:
    //   - has a composite index on (campaignType, salonId, sentAt)
    //   - has an explicit permissive 'list' rule (just auth + salonUsers existence)
    //   - uses customerId = Firestore document ID (no uid mismatch)
    //   - is written by recordCampaignDeliveries() after every actual blast
    const deliveriesRef = collection(db, 'campaignDeliveries');
    const deliveriesQuery = query(
      deliveriesRef,
      where('campaignType', '==', campaignType),
      where('salonId', '==', salonId),
      where('sentAt', '>=', Timestamp.fromDate(startTime))
    );

    const snapshot = await getDocs(deliveriesQuery);
    const customerDeliveryCounts = {};

    snapshot.forEach(docSnap => {
      const data = docSnap.data();
      const cid = data.customerId;
      if (cid) {
        customerDeliveryCounts[cid] = (customerDeliveryCounts[cid] || 0) + 1;
      }
    });

    return customerDeliveryCounts;
  } catch (error) {
    console.error('Error counting deliveries from campaignDeliveries:', error);
    return {};
  }
};

/**
 * Get customers after excluding those who have unsubscribed
 */
export const getCustomersAfterUnsubscribeExclusion = async (
  salonId,
  campaignType,
  targetingOptions
) => {
  try {
    // First get all targeted customers based on criteria
    const basePreview = await getCustomerTargetingPreview(salonId, targetingOptions);

    // Determine the notification preference field based on campaign type
    let notificationField;
    switch(campaignType) {
      case 'email':
        notificationField = 'preferences.notifications.email';
        break;
      case 'whatsapp':
        notificationField = 'preferences.notifications.whatsapp';
        break;
      case 'push':
        notificationField = 'preferences.notifications.push';
        break;
      case 'social_media':
        notificationField = 'preferences.notifications.socialMedia';
        break;
      default:
        notificationField = 'preferences.notifications.email';
    }

    // Filter out customers who have unsubscribed
    const unsubscribedCustomers = [];
    const eligibleCustomers = basePreview.eligibleCustomers.filter(customer => {
      // Check legacy preferences.notifications.* field
      const notificationEnabled = notificationField.split('.').reduce((obj, key) => obj?.[key], customer);
      // Also check subscriptionPreferences.email set by the one-click unsubscribe link
      const emailUnsub = campaignType === 'email' &&
        customer.subscriptionPreferences?.email === false;
      const hasUnsubscribed = notificationEnabled === false || emailUnsub;

      if (hasUnsubscribed) {
        unsubscribedCustomers.push({
          ...customer,
          exclusionReason: `Unsubscribed from ${campaignType} communications`
        });
        return false;
      }
      return true;
    });

    return {
      totalCount: basePreview.totalCount,
      eligibleCount: eligibleCustomers.length,
      eligibleCustomers,
      unsubscribedCount: unsubscribedCustomers.length,
      unsubscribedCustomers
    };
  } catch (error) {
    console.error('Error getting customers after unsubscribe exclusion:', error);
    throw error;
  }
};

/**
 * Get customers after excluding those who hit frequency limits
 */
export const getCustomersAfterFrequencyExclusion = async (
  salonId,
  campaignType,
  targetingOptions
) => {
  try {
    // First get customers after unsubscribe exclusion
    const afterUnsubscribe = await getCustomersAfterUnsubscribeExclusion(
      salonId,
      campaignType,
      targetingOptions
    );

    // Get frequency settings for this campaign type
    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (!salonDoc.exists()) {
      return {
        ...afterUnsubscribe,
        frequencyExcludedCustomers: [],
        frequencyExcludedCount: 0
      };
    }

    const frequencySettings = salonDoc.data().campaignFrequency?.[campaignType];

    if (!frequencySettings) {
      // No frequency limits set
      return {
        ...afterUnsubscribe,
        frequencyExcludedCustomers: [],
        frequencyExcludedCount: 0
      };
    }

    // Calculate time window based on interval
    const now = new Date();
    let startTime;

    switch(frequencySettings.interval) {
      case 'day':
        startTime = new Date(now.getTime() - (24 * 60 * 60 * 1000));
        break;
      case 'week':
        startTime = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
        break;
      case 'month':
        startTime = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        break;
      default:
        startTime = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    }

    // Count deliveries from the actual campaign collection (pushNotifications/mail/whatsappMessages)
    const customerDeliveryCounts = await countDeliveriesFromCampaignCollection(
      salonId,
      campaignType,
      startTime,
      afterUnsubscribe.eligibleCustomers
    );

    // Filter out customers who have reached the limit
    const frequencyExcludedCustomers = [];
    const eligibleCustomers = afterUnsubscribe.eligibleCustomers.filter(customer => {
      const deliveryCount = customerDeliveryCounts[customer.id] || 0;
      const hasReachedLimit = deliveryCount >= frequencySettings.maxFrequency;

      if (hasReachedLimit) {
        frequencyExcludedCustomers.push({
          ...customer,
          deliveryCount,
          limit: frequencySettings.maxFrequency,
          interval: frequencySettings.interval,
          exclusionReason: `Received ${deliveryCount} of ${frequencySettings.maxFrequency} allowed messages in the past ${frequencySettings.interval}`
        });
        return false;
      }
      return true;
    });

    return {
      totalCount: afterUnsubscribe.totalCount,
      eligibleCount: eligibleCustomers.length,
      eligibleCustomers,
      unsubscribedCount: afterUnsubscribe.unsubscribedCount,
      unsubscribedCustomers: afterUnsubscribe.unsubscribedCustomers,
      frequencyExcludedCount: frequencyExcludedCustomers.length,
      frequencyExcludedCustomers,
      frequencySettings
    };
  } catch (error) {
    console.error('Error getting customers after frequency exclusion:', error);
    throw error;
  }
};

/**
 * Get customers after excluding recipients from earlier runs (for recurring campaigns)
 */
export const getCustomersAfterRecurrenceExclusion = async (
  salonId,
  campaignType,
  targetingOptions,
  recurringSettings
) => {
  try {
    // First get customers after frequency exclusion
    const afterFrequency = await getCustomersAfterFrequencyExclusion(
      salonId,
      campaignType,
      targetingOptions
    );

    // If not a recurring campaign or no exclusion settings, return as is
    if (!recurringSettings || !recurringSettings.excludePreviousRecipients) {
      return {
        ...afterFrequency,
        recurrenceExcludedCount: 0,
        recurrenceExcludedCustomers: []
      };
    }

    // Determine the cutoff date/cycles for exclusion
    let cutoffDate;

    if (recurringSettings.exclusionType === 'date') {
      cutoffDate = new Date(recurringSettings.excludeBeforeDate);
    } else {
      // exclusionType === 'cycles'
      // For simplicity, we'll look back at the last N cycles worth of time
      const cyclesBack = recurringSettings.excludePastCycles || 1;
      const now = new Date();

      switch(recurringSettings.frequency) {
        case 'daily':
          cutoffDate = new Date(now.getTime() - (cyclesBack * 24 * 60 * 60 * 1000));
          break;
        case 'weekly':
          cutoffDate = new Date(now.getTime() - (cyclesBack * 7 * 24 * 60 * 60 * 1000));
          break;
        case 'monthly':
          cutoffDate = new Date(now.getTime() - (cyclesBack * 30 * 24 * 60 * 60 * 1000));
          break;
        case 'annually':
          cutoffDate = new Date(now.getTime() - (cyclesBack * 365 * 24 * 60 * 60 * 1000));
          break;
        default:
          cutoffDate = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      }
    }

    // Query for previous campaign deliveries since cutoff
    // Order matches composite index: campaignType, salonId, sentAt
    const deliveriesRef = collection(db, 'campaignDeliveries');
    const deliveriesQuery = query(
      deliveriesRef,
      where('campaignType', '==', campaignType),
      where('salonId', '==', salonId),
      where('sentAt', '>=', Timestamp.fromDate(cutoffDate))
    );

    const deliveriesSnapshot = await getDocs(deliveriesQuery);

    // Build set of customer IDs who received messages since cutoff
    const previousRecipientIds = new Set();
    deliveriesSnapshot.forEach(doc => {
      const data = doc.data();
      previousRecipientIds.add(data.customerId);
    });

    // Filter out customers who were previous recipients
    const recurrenceExcludedCustomers = [];
    const eligibleCustomers = afterFrequency.eligibleCustomers.filter(customer => {
      const wasPreviousRecipient = previousRecipientIds.has(customer.id);

      if (wasPreviousRecipient) {
        recurrenceExcludedCustomers.push({
          ...customer,
          exclusionReason: 'Received this campaign in a previous cycle'
        });
        return false;
      }
      return true;
    });

    return {
      totalCount: afterFrequency.totalCount,
      eligibleCount: eligibleCustomers.length,
      eligibleCustomers,
      unsubscribedCount: afterFrequency.unsubscribedCount,
      unsubscribedCustomers: afterFrequency.unsubscribedCustomers,
      frequencyExcludedCount: afterFrequency.frequencyExcludedCount,
      frequencyExcludedCustomers: afterFrequency.frequencyExcludedCustomers,
      recurrenceExcludedCount: recurrenceExcludedCustomers.length,
      recurrenceExcludedCustomers,
      frequencySettings: afterFrequency.frequencySettings,
      recurringSettings
    };
  } catch (error) {
    console.error('Error getting customers after recurrence exclusion:', error);
    throw error;
  }
};

/**
 * Get final nett customer list after all exclusions
 */
export const getFinalCustomerList = async (
  salonId,
  campaignType,
  targetingOptions,
  recurringSettings = null
) => {
  try {
    // Get customers after all exclusions
    const result = await getCustomersAfterRecurrenceExclusion(
      salonId,
      campaignType,
      targetingOptions,
      recurringSettings
    );

    // Calculate summary information
    const afterTargeting = result.eligibleCustomers.length +
                          (result.unsubscribedCount || 0) +
                          result.frequencyExcludedCount +
                          result.recurrenceExcludedCount;

    const totalExcluded = (result.unsubscribedCount || 0) +
                         result.frequencyExcludedCount +
                         result.recurrenceExcludedCount;
    const exclusionRate = afterTargeting > 0
      ? ((totalExcluded / afterTargeting) * 100).toFixed(1)
      : '0.0';

    return {
      ...result,
      summary: {
        totalCustomers: result.totalCount,
        afterTargeting: afterTargeting,
        excludedByUnsubscribe: result.unsubscribedCount || 0,
        excludedByFrequency: result.frequencyExcludedCount,
        excludedByRecurrence: result.recurrenceExcludedCount,
        totalExcluded: totalExcluded,
        finalCount: result.eligibleCount,
        exclusionRate: exclusionRate
      }
    };
  } catch (error) {
    console.error('Error getting final customer list:', error);
    throw error;
  }
};

/**
 * Record campaign deliveries to the campaignDeliveries collection.
 * Written after a successful campaign send for future query optimization.
 * Errors are caught and logged but do not block the campaign.
 *
 * @param {string} salonId
 * @param {string} campaignType - 'push' | 'email' | 'whatsapp'
 * @param {Array} customers - array of customer objects with at least { id }
 */
export const recordCampaignDeliveries = async (salonId, campaignType, customers) => {
  try {
    const deliveriesRef = collection(db, 'campaignDeliveries');
    const writePromises = customers.map(customer =>
      addDoc(deliveriesRef, {
        customerId: customer.id,
        salonId,
        campaignType,
        sentAt: Timestamp.now()
      })
    );
    await Promise.all(writePromises);
  } catch (error) {
    console.error('Error recording campaign deliveries (non-blocking):', error);
  }
};

/**
 * Apply frequency exclusion to a customer list before sending a blast.
 * Reads frequency settings from the salon doc and counts actual deliveries
 * from the campaign collection to determine which customers are eligible.
 *
 * @param {string} salonId
 * @param {string} campaignType - 'push' | 'email' | 'whatsapp'
 * @param {Array} customers - array of customer objects
 * @returns {{ eligible: Array, excluded: Array }}
 */
export const applyFrequencyExclusionToList = async (salonId, campaignType, customers) => {
  try {
    const salonRef = doc(db, 'salons', salonId);
    const salonDoc = await getDoc(salonRef);

    if (!salonDoc.exists()) {
      return { eligible: customers, excluded: [] };
    }

    const frequencySettings = salonDoc.data().campaignFrequency?.[campaignType];

    if (!frequencySettings || !frequencySettings.maxFrequency) {
      return { eligible: customers, excluded: [] };
    }

    // Calculate time window based on interval
    const now = new Date();
    let startTime;

    switch (frequencySettings.interval) {
      case 'day':
        startTime = new Date(now.getTime() - (24 * 60 * 60 * 1000));
        break;
      case 'week':
        startTime = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
        break;
      case 'month':
        startTime = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        break;
      default:
        startTime = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    }

    const customerDeliveryCounts = await countDeliveriesFromCampaignCollection(
      salonId,
      campaignType,
      startTime,
      customers
    );

    const eligible = [];
    const excluded = [];

    customers.forEach(customer => {
      const deliveryCount = customerDeliveryCounts[customer.id] || 0;
      if (deliveryCount >= frequencySettings.maxFrequency) {
        excluded.push({
          ...customer,
          deliveryCount,
          limit: frequencySettings.maxFrequency,
          interval: frequencySettings.interval
        });
      } else {
        eligible.push(customer);
      }
    });

    return { eligible, excluded };
  } catch (error) {
    console.error('Error applying frequency exclusion (non-blocking):', error);
    // Fail gracefully - don't block the campaign
    return { eligible: customers, excluded: [] };
  }
};
